/**
 * Feature flags for controlling application behavior
 *
 * These flags allow toggling features on/off via environment variables
 * without requiring code changes.
 */
const featureFlags = {
  /**
   * Whether to use CMS API for content pages
   * When true: Fetches content from backend API (requires API to be running)
   * When false: Uses hardcoded content in the components (default, safe fallback)
   *
   * Environment variable: REACT_APP_USE_CMS_CONTENT
   *
   * @returns {boolean}
   */
  useCmsContent: () => {
    return process.env.REACT_APP_USE_CMS_CONTENT === 'true';
  },

  /**
   * Check if Google OAuth is enabled
   * @returns {boolean}
   */
  useGoogleOAuth: () => {
    return process.env.REACT_APP_ENABLE_GOOGLE_OAUTH === 'true';
  },

  /**
   * Check if Stripe payments are enabled
   * @returns {boolean}
   */
  useStripe: () => {
    return process.env.REACT_APP_ENABLE_STRIPE === 'true';
  },

  /**
   * Check if PayPal payments are enabled
   * @returns {boolean}
   */
  usePayPal: () => {
    return process.env.REACT_APP_ENABLE_PAYPAL === 'true';
  },

  /**
   * Check if newsletter signup is enabled
   * @returns {boolean}
   */
  useNewsletter: () => {
    return process.env.REACT_APP_ENABLE_NEWSLETTER === 'true';
  },

  /**
   * Check if debug mode is enabled
   * @returns {boolean}
   */
  isDebugMode: () => {
    return process.env.REACT_APP_DEBUG === 'true';
  },

  /**
   * Check if WebSocket real-time updates are enabled
   * When true: Frontend connects to WebSocket and receives real-time content updates
   * When false: Frontend works normally but without real-time updates
   * @returns {boolean}
   */
  useWebSocketUpdates: () => {
    return process.env.REACT_APP_ENABLE_WEBSOCKET === 'true';
  },

  /**
   * Check if dynamic hero carousel is enabled
   * When true: Fetches carousel slides from CMS API
   * When false: Uses hardcoded slides (safe fallback)
   *
   * Environment variable: REACT_APP_USE_DYNAMIC_CAROUSEL
   *
   * @returns {boolean}
   */
  useDynamicCarousel: () => {
    return process.env.REACT_APP_USE_DYNAMIC_CAROUSEL === 'true';
  },

  /**
   * Check if dynamic page backgrounds are enabled
   * When true: Fetches page background images from CMS API
   * When false: Uses hardcoded background images (safe fallback)
   *
   * Environment variable: REACT_APP_USE_DYNAMIC_BACKGROUNDS
   *
   * @returns {boolean}
   */
  useDynamicBackgrounds: () => {
    return process.env.REACT_APP_USE_DYNAMIC_BACKGROUNDS === 'true';
  },

  /**
   * Check if new Stripe PaymentIntent flow is enabled
   * When true: Uses embedded Stripe Elements with PaymentIntent API
   * When false: Uses old redirect-based Stripe Checkout flow
   *
   * Environment variable: REACT_APP_ENABLE_STRIPE_PAYMENT_INTENT
   *
   * @returns {boolean}
   */
  useStripePaymentIntent: () => {
    return process.env.REACT_APP_ENABLE_STRIPE_PAYMENT_INTENT === 'true';
  },

  /**
   * Check if old Stripe redirect flow should be used
   * Inverse of useStripePaymentIntent for clarity
   *
   * @returns {boolean}
   */
  useStripeRedirect: () => {
    return !featureFlags.useStripePaymentIntent();
  }
};

export default featureFlags;
